import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { fileUrl, formatBRL, formatKM } from "@/lib/api";
import { useCompare } from "@/context/CompareContext";
import { useFavorites } from "@/context/FavoritesContext";
import { useAuth } from "@/context/AuthContext";
import { Gauge, Calendar, Eye, Star, Gift, GitCompareArrows, Check, TrendingDown, Heart } from "lucide-react";

export function CarCard({ car, index = 0 }) {
  const compare = useCompare();
  const favorites = useFavorites();
  const { user } = useAuth();
  const navigate = useNavigate();
  const selected = compare?.has(car.id);
  const faved = favorites?.has(car.id);
  const img = car.images?.[0] ? fileUrl(car.images[0]) : "https://images.unsplash.com/photo-1630356220662-4d9804d3a987";
  const fipeDiff = car.fipe_price && car.preco < car.fipe_price
    ? Math.round(((car.fipe_price - car.preco) / car.fipe_price) * 100) : null;

  const onFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.info("Entre para salvar favoritos."); navigate("/entrar", { state: { from: `/anuncio/${car.id}` } }); return; }
    try {
      const now = await favorites.toggle(car.id);
      toast.success(now ? "Adicionado aos favoritos!" : "Removido dos favoritos.");
    } catch { toast.error("Erro ao favoritar."); }
  };

  const onCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    compare?.toggle(car.id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.4) }}
    >
      <Link to={`/anuncio/${car.id}`} data-testid={`car-card-${car.id}`}
        className="group block bg-white border border-border rounded-sm overflow-hidden h-full hover:border-obsidian transition-colors">
        <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
          <img src={img} alt={`${car.marca} ${car.modelo}`}
            className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${car.status === "sold" ? "grayscale opacity-70" : ""}`}
            style={{ transitionProperty: "transform" }} />
          <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
            {car.featured && (
              <span className="bg-blaze text-white text-[11px] font-800 uppercase tracking-wide px-2 py-1 rounded-sm flex items-center gap-1" data-testid={`featured-badge-${car.id}`}>
                <Star className="w-3 h-3 fill-white" /> Destaque
              </span>
            )}
            <span className="bg-obsidian text-white text-[11px] font-700 px-2 py-1 rounded-sm">{car.ano}</span>
            {car.status !== "sold" && (
              <span className="bg-green-600 text-white text-[11px] font-800 px-2 py-1 rounded-sm flex items-center gap-1" data-testid={`free-badge-${car.id}`}>
                <Gift className="w-3 h-3" /> Anúncio grátis
              </span>
            )}
          </div>
          <button onClick={onFavorite} data-testid={`favorite-toggle-${car.id}`} aria-label="Favoritar"
            className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-sm transition-colors ${faved ? "bg-blaze text-white" : "bg-white/80 text-obsidian hover:bg-white"}`}>
            <Heart className={`w-4 h-4 ${faved ? "fill-white" : ""}`} strokeWidth={1.8} />
          </button>
          {car.status === "sold" && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="bg-blaze text-white font-poster text-3xl px-5 py-1.5 -rotate-6 shadow-xl" data-testid={`card-sold-${car.id}`}>VENDIDO</span>
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/50 to-transparent flex items-end px-3 pb-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="text-white text-xs font-600 flex items-center gap-1"><Eye className="w-3 h-3" /> Ver anúncio</span>
          </div>
        </div>
        <div className="p-4">
          <p className="font-heading font-800 text-2xl leading-none text-obsidian">{formatBRL(car.preco)}</p>
          {fipeDiff !== null && fipeDiff > 0 && car.status !== "sold" && (
            <span className="mt-2 inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-[11px] font-800 px-2 py-0.5 rounded-sm" data-testid={`fipe-below-badge-${car.id}`}>
              <TrendingDown className="w-3 h-3" /> {fipeDiff}% abaixo da FIPE
            </span>
          )}
          <p className="mt-2 font-700 text-sm text-obsidian truncate">{car.marca} {car.modelo}</p>
          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Gauge className="w-3.5 h-3.5" strokeWidth={1.5} /> {formatKM(car.km)}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" strokeWidth={1.5} /> {car.ano}</span>
          </div>
          {(car.cidade || car.estado) && (
            <p className="mt-2 text-xs text-muted-foreground truncate">{car.cidade}{car.estado ? ` - ${car.estado}` : ""}</p>
          )}
          {compare && (
            <button onClick={onCompare} data-testid={`compare-toggle-${car.id}`}
              disabled={!selected && compare.full}
              className={`mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-700 rounded-sm py-2 border transition-colors ${selected ? "bg-obsidian text-white border-obsidian" : "border-border text-muted-foreground hover:border-obsidian hover:text-obsidian disabled:opacity-40 disabled:cursor-not-allowed"}`}>
              {selected ? <><Check className="w-3.5 h-3.5" /> Selecionado</> : <><GitCompareArrows className="w-3.5 h-3.5" /> Comparar</>}
            </button>
          )}
        </div>
      </Link>
    </motion.div>
  );
}
